import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useMemo, useState } from "react";
import {
  ApiKeyAdminGetDocument,
  ApiKeyAdminListDocument,
  useApiKeyAdminConnectOrgsMutation,
  useApiKeyAdminSearchOrganizationsQuery,
} from "../../../generated/graphql";
import { throwOnGraphqlErrors } from "../throw-on-graphql-errors";

const ORG_SEARCH_LIMIT = 50;

const styles = {
  container: { pt: 1 } satisfies SxProps<Theme>,
  resultsBox: {
    maxHeight: 360,
    overflow: "auto",
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 1,
  } satisfies SxProps<Theme>,
  selectedBox: { mt: 1 } satisfies SxProps<Theme>,
};

type ConnectOrgsDialogProps = {
  readonly open: boolean;
  readonly apiKeyUuid: string;
  readonly alreadyLinkedOrgUuids: Set<string>;
  readonly onClose: () => void;
};

/**
 * Dialog that searches active organizations across ALL companies and
 * lets the admin multi-select rows to bulk-link to the key. A "Select all"
 * control selects every currently-listed organization that is not already
 * linked, and "Clear" empties the pending selection.
 */
const ConnectOrgsDialog = ({
  open,
  apiKeyUuid,
  alreadyLinkedOrgUuids,
  onClose,
}: ConnectOrgsDialogProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState<
    Map<string, { companyName: string; organizationDisplayName: string }>
  >(new Map());
  const [error, setError] = useState<string | null>(null);

  const { data, loading } = useApiKeyAdminSearchOrganizationsQuery({
    variables: {
      input: {
        searchTerm: searchTerm.trim() === "" ? null : searchTerm.trim(),
        limit: ORG_SEARCH_LIMIT,
      },
    },
    skip: !open,
    fetchPolicy: "cache-and-network",
  });

  const [connectOrgs, { loading: connecting }] =
    useApiKeyAdminConnectOrgsMutation({
      refetchQueries: [ApiKeyAdminGetDocument, ApiKeyAdminListDocument],
    });

  const results = useMemo(
    () => data?.apiKeyAdminSearchOrganizations ?? [],
    [data],
  );

  const toggle = (
    uuid: string,
    label: {
      companyName: string;
      organizationDisplayName: string;
    },
  ) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.set(uuid, label);
      }
      return next;
    });
  };

  const selectedArray = useMemo(() => [...selected.entries()], [selected]);

  const selectableResults = useMemo(
    () =>
      results.filter((org) => !alreadyLinkedOrgUuids.has(org.organizationUuid)),
    [results, alreadyLinkedOrgUuids],
  );

  const allSelectableSelected = useMemo(
    () =>
      selectableResults.length > 0 &&
      selectableResults.every((org) => selected.has(org.organizationUuid)),
    [selectableResults, selected],
  );

  const handleSelectAll = () => {
    setSelected((prev) => {
      const next = new Map(prev);
      for (const org of selectableResults) {
        next.set(org.organizationUuid, {
          companyName: org.companyName,
          organizationDisplayName: org.organizationDisplayName,
        });
      }
      return next;
    });
  };

  const handleClearSelection = () => {
    setSelected(new Map());
  };

  const handleConnect = async () => {
    if (selected.size === 0) {
      return;
    }
    setError(null);
    try {
      const result = await connectOrgs({
        variables: {
          input: {
            apiKeyUuid,
            organizationUuids: [...selected.keys()],
          },
        },
      });
      throwOnGraphqlErrors(result);
      setSelected(new Map());
      setSearchTerm("");
      onClose();
    } catch (error_) {
      const message =
        error_ instanceof Error ? error_.message : "Unknown error";
      setError(message);
    }
  };

  const handleClose = () => {
    setSelected(new Map());
    setSearchTerm("");
    setError(null);
    onClose();
  };

  return (
    <Dialog fullWidth open={open} maxWidth="md" onClose={handleClose}>
      <DialogTitle>Add organizations</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={styles.container}>
          {error !== null && <Alert severity="error">{error}</Alert>}
          <TextField
            fullWidth
            autoFocus
            label="Search by organization name"
            placeholder="Type at least one character to narrow results"
            value={searchTerm}
            size="small"
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              disabled={selectableResults.length === 0 || allSelectableSelected}
              onClick={handleSelectAll}
            >
              Select all ({selectableResults.length})
            </Button>
            <Button
              size="small"
              color="inherit"
              disabled={selected.size === 0}
              onClick={handleClearSelection}
            >
              Clear
            </Button>
          </Stack>
          <Box sx={styles.resultsBox}>
            <TableContainer>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>Company</TableCell>
                    <TableCell>Organization</TableCell>
                    <TableCell>Company UUID</TableCell>
                    <TableCell>Organization UUID</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <CircularProgress size={20} />
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && results.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No organizations match.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {results.map((org) => {
                    const isLinked = alreadyLinkedOrgUuids.has(
                      org.organizationUuid,
                    );
                    const isSelected = selected.has(org.organizationUuid);
                    return (
                      <TableRow
                        key={org.organizationUuid}
                        hover
                        sx={{
                          cursor: isLinked ? "default" : "pointer",
                          opacity: isLinked ? 0.5 : 1,
                        }}
                        onClick={() => {
                          if (!isLinked) {
                            toggle(org.organizationUuid, {
                              companyName: org.companyName,
                              organizationDisplayName:
                                org.organizationDisplayName,
                            });
                          }
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            disabled={isLinked}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            onChange={() => {
                              if (!isLinked) {
                                toggle(org.organizationUuid, {
                                  companyName: org.companyName,
                                  organizationDisplayName:
                                    org.organizationDisplayName,
                                });
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {org.companyName}
                          {isLinked && (
                            <Chip
                              size="small"
                              label="already linked"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>{org.organizationDisplayName}</TableCell>
                        <TableCell>
                          <Typography variant="caption" fontFamily="monospace">
                            {org.companyUuid}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" fontFamily="monospace">
                            {org.organizationUuid}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          <Box sx={styles.selectedBox}>
            <Typography gutterBottom variant="body2" color="text.secondary">
              Selected ({selected.size})
            </Typography>
            <Stack useFlexGap direction="row" spacing={1} flexWrap="wrap">
              {selectedArray.map(([uuid, label]) => (
                <Chip
                  key={uuid}
                  label={`${label.companyName} / ${label.organizationDisplayName}`}
                  onDelete={() => {
                    toggle(uuid, label);
                  }}
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={connecting} onClick={handleClose}>
          Cancel
        </Button>
        <Button
          disabled={selected.size === 0 || connecting}
          variant="contained"
          onClick={handleConnect}
        >
          {connecting ? "Connecting…" : `Connect ${selected.size} selected`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConnectOrgsDialog;
